/**
 * Pure state machine for Apple-Center-Stage-style auto-framing and its
 * coupling with the virtual/blur background. Dependency-free (no store /
 * mediaManager imports) so the rules are unit-testable in isolation — and
 * so those tests run from a worktree where the `@os/messenger` alias that
 * voiceStore pulls in would not resolve.
 *
 * Three pieces of state:
 *  - pref:           the user's explicit long-term choice (store persists it).
 *  - effective:      what the tracker does right now / what the button shows.
 *  - suppressedByBg: effective is off ONLY because a background is active.
 *
 * Invariant: effective === true implies pref === true. (effective only ever
 * becomes true via a manual-on, a restore that required a prior suppression,
 * or a join where pref was on.)
 */
export interface AutoFrameState {
  pref: boolean
  effective: boolean
  suppressedByBg: boolean
}

/** Rule 1 — user clicks the auto-frame button. Explicit choice wins and is remembered. */
export function applyManualToggle(_prev: AutoFrameState, next: boolean): AutoFrameState {
  return { pref: next, effective: next, suppressedByBg: false }
}

/** Rules 2 & 3 — the background turned on (bgActive=true) or off (false). */
export function applyBackgroundChange(prev: AutoFrameState, bgActive: boolean): AutoFrameState {
  if (bgActive) {
    // Rule 2: suppress auto-frame if it is currently on. pref untouched.
    if (prev.effective) return { ...prev, effective: false, suppressedByBg: true }
    return prev
  }
  // Rule 3: restore only when the off was an automatic suppression.
  if (prev.suppressedByBg) return { ...prev, effective: true, suppressedByBg: false }
  return prev
}

/** Rule 4 — compute state on (re)joining a room/call. Background wins. */
export function computeJoinState(pref: boolean, bgActive: boolean): AutoFrameState {
  if (pref && bgActive) return { pref, effective: false, suppressedByBg: true }
  return { pref, effective: pref, suppressedByBg: false }
}
